import React, { useState, useEffect } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '@obis/firebase-config';
import { RocketLaunchIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';

interface GoLiveProps {
  tenantId: string;
}

const GoLiveCountdown: React.FC<GoLiveProps> = ({ tenantId }) => {
  const [data, setData] = useState({
    startDate: new Date(),
    targetDays: 14,
    currentDay: 1,
    isDelayed: false,
    loading: true
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const docRef = doc(db, 'onboardingFlows', tenantId);
        const snap = await getDoc(docRef);
        if (snap.exists()) {
          const flow = snap.data();
          const targetDays = flow.targetGoLiveDays || 14;
          
          let start = new Date();
          if (flow.onboardingStartDate) {
             start = flow.onboardingStartDate.toDate();
          }

          const now = new Date();
          const diffTime = Math.abs(now.getTime() - start.getTime());
          const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
          
          setData({
            startDate: start,
            targetDays,
            currentDay: diffDays,
            isDelayed: diffDays > targetDays,
            loading: false
          });
        } else {
          setData(prev => ({ ...prev, loading: false }));
        }
      } catch (err) {
        console.error("Error fetching go-live tracking", err);
        setData(prev => ({ ...prev, loading: false }));
      }
    };
    if (tenantId) fetchData();
  }, [tenantId]);

  if (data.loading) return <div className="animate-pulse bg-gray-100 h-24 rounded-2xl"></div>;

  return (
    <div className={`p-5 rounded-2xl border shadow-sm flex items-center justify-between ${
      data.isDelayed 
        ? 'bg-red-50 border-red-200' 
        : 'bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-100'
    }`}>
      <div className="flex items-center gap-4">
        <div className={`p-3 rounded-xl ${data.isDelayed ? 'bg-red-100 text-red-600' : 'bg-blue-100 text-blue-600'}`}>
          {data.isDelayed ? <ExclamationTriangleIcon className="w-6 h-6" /> : <RocketLaunchIcon className="w-6 h-6" />}
        </div>
        <div>
          <h4 className={`font-bold text-lg ${data.isDelayed ? 'text-red-800' : 'text-blue-900'}`}>
            {data.isDelayed ? 'Go-Live Delayed' : 'Go-Live Target'}
          </h4>
          <p className={`text-sm font-medium ${data.isDelayed ? 'text-red-600' : 'text-blue-700/80'}`}>
            Onboarding started {data.startDate.toLocaleDateString()}
          </p>
        </div>
      </div>
      
      <div className="text-right">
        <div className="text-2xl font-black tracking-tight" style={{ color: data.isDelayed ? '#dc2626' : '#1e40af' }}>
          Day {data.currentDay} <span className="text-opacity-50 text-base font-bold">/ {data.targetDays}</span>
        </div>
        <p className={`text-xs font-bold uppercase tracking-wider mt-1 ${data.isDelayed ? 'text-red-500' : 'text-blue-500'}`}>
          {data.isDelayed 
            ? `${data.currentDay - data.targetDays} Days Overdue` 
            : `${data.targetDays - data.currentDay} Days Remaining`}
        </p>
      </div>
    </div>
  );
};

export default GoLiveCountdown;
